import { isMultiVisitRequestTurn, isVisitSchedulingGoal } from '@/lib/visit-board';
import { resolveMultiProjectMentions } from '@/lib/engaged-projects';
import { isFocusedPoiAsk } from '@/lib/search-refine';

export type BoardProjectTab = 'legal' | 'units' | 'price' | 'emi';

export interface ProjectRef {
  id: string;
  name: string;
}

export function topicToProjectTab(topic?: string): BoardProjectTab {
  switch (topic) {
    case 'price':
      return 'price';
    case 'emi':
      return 'emi';
    case 'availability':
    case 'property_type':
      return 'units';
    case 'legal':
      return 'legal';
    default:
      return 'legal';
  }
}

/** Match a project name (or distinctive tail, e.g. Eldorado) in free text. */
export function resolveProjectMention(
  text: string,
  projects: readonly ProjectRef[],
): ProjectRef | undefined {
  const t = text.toLowerCase();
  for (const p of projects) {
    const name = p.name.toLowerCase();
    if (t.includes(name)) return p;
  }
  for (const p of projects) {
    const parts = p.name.toLowerCase().split(/\s+/).filter((w) => w.length >= 5);
    for (const part of parts) {
      if (t.includes(part)) return p;
    }
  }
  return undefined;
}

function wantsCompareBoard(userText: string, goalTopic?: string): boolean {
  if (goalTopic === 'compare') return true;
  if (/\bcompare\b/i.test(userText)) return true;
  return /\b(?:which (?:one )?fits|better for my budget|which is better|recommend between)\b/i.test(
    userText,
  );
}

function wantsMatchesBoard(userText: string, goalKind?: string, phase?: string): boolean {
  // "Schools near my matches" is a POI facet, not "back to my matches".
  if (isFocusedPoiAsk(userText)) return false;
  if (goalKind === 'recommend' || goalKind === 'ack_reject_recommend') return true;
  if (phase === 'discover' && goalKind === 'recommend') return true;
  return /\b(?:show me more|other projects|more options|more projects|back to matches|my matches)\b/i.test(
    userText,
  );
}

function inferTabFromText(userText: string): BoardProjectTab | null {
  if (/\b(?:legal|title|rera|khata|litigation|approval|ec status)\b/i.test(userText)) return 'legal';
  if (/\b(?:amenit|facilit|clubhouse|pool|gym)\b/i.test(userText)) return 'legal';
  if (/\bemi\b/i.test(userText)) return 'emi';
  if (/\b(?:price|pricing|cost|starting from|how much)\b/i.test(userText)) return 'price';
  if (/\b(?:unit type|configuration|bhk|floor plan)\b/i.test(userText)) return 'units';
  return null;
}

export type BoardRouteAction =
  | { action: 'none' }
  | { action: 'compare' }
  | { action: 'matches' }
  | { action: 'visit' }
  | { action: 'project'; projectId: string; tab: BoardProjectTab };

export interface BoardRouteInput {
  userText: string;
  goalKind?: string;
  goalTopic?: string;
  goalProjectId?: string;
  phase?: string;
  visitBooked?: boolean;
  projects: readonly ProjectRef[];
}

export function boardRouteFromNba(nba: {
  board: string;
  board_tab?: string;
  board_project_id?: string;
}): BoardRouteAction {
  switch (nba.board) {
    case 'compare':
      return { action: 'compare' };
    case 'matches':
      return { action: 'matches' };
    case 'visit':
      return { action: 'visit' };
    case 'project': {
      const projectId = nba.board_project_id;
      if (!projectId) return { action: 'none' };
      const tab = (nba.board_tab === 'overview' ? 'legal' : nba.board_tab) as BoardProjectTab | undefined;
      return {
        action: 'project',
        projectId,
        tab: tab && ['legal', 'units', 'price', 'emi'].includes(tab) ? tab : 'legal',
      };
    }
    default:
      return { action: 'none' };
  }
}

export function boardRouteAction(input: BoardRouteInput): BoardRouteAction {
  if (input.visitBooked && input.goalKind === 'visit_booked') {
    return { action: 'visit' };
  }

  if (wantsCompareBoard(input.userText, input.goalTopic)) {
    return { action: 'compare' };
  }

  if (wantsMatchesBoard(input.userText, input.goalKind, input.phase)) {
    return { action: 'matches' };
  }

  if (/\b(?:plan (?:a )?visit|visit day|site visit|plan visits)\b/i.test(input.userText)) {
    return { action: 'visit' };
  }

  if (isMultiVisitRequestTurn(input.userText, input.projects)) {
    return { action: 'visit' };
  }

  if (
    /\bvisit\b/i.test(input.userText) &&
    resolveMultiProjectMentions(input.userText, input.projects).length >= 2
  ) {
    return { action: 'visit' };
  }

  if (input.goalKind === 'answer' && input.goalTopic && input.goalTopic !== 'compare') {
    const tab = topicToProjectTab(input.goalTopic);
    const pid =
      input.goalProjectId ??
      resolveProjectMention(input.userText, input.projects)?.id;
    if (pid) return { action: 'project', projectId: pid, tab };
  }

  if (input.goalKind === 'commit' && input.goalProjectId) {
    const tab = topicToProjectTab(input.goalTopic);
    return { action: 'project', projectId: input.goalProjectId, tab };
  }

  if (input.goalKind === 'objection') {
    const pid =
      input.goalProjectId ?? resolveProjectMention(input.userText, input.projects)?.id;
    if (pid) return { action: 'project', projectId: pid, tab: 'legal' };
  }

  const textTab = inferTabFromText(input.userText);
  const mention = resolveProjectMention(input.userText, input.projects);
  if (textTab && mention) {
    return { action: 'project', projectId: mention.id, tab: textTab };
  }

  if (/\b(?:tell me about|more about|details on|info on)\b/i.test(input.userText) && mention) {
    return { action: 'project', projectId: mention.id, tab: textTab ?? 'legal' };
  }

  if (
    isVisitSchedulingGoal(input.goalKind) &&
    input.goalKind !== 'visit_booked' &&
    /\b(?:plan (?:a )?visit|visit day|site visit)\b/i.test(input.userText)
  ) {
    return { action: 'visit' };
  }

  return { action: 'none' };
}
